// Import global CSS first to ensure it loads before components
import './styles/global.css';

import { LitElement, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { styles } from './styles/shared-styles';
import { router } from './router';
import { SubscriptionService } from './services/subscription-service';
import { LocationService } from './services/location-service';
import { t } from './translations';

import './pages/app-home/app-home';
import './pages/app-map/app-map';
import './pages/app-settings/app-settings';
import './components/globe';
import './pages/app-aurora/app-aurora';

export interface LocationUpdateEvent extends CustomEvent {
  detail: {
    latitude: number;
    longitude: number;
  };
}

@customElement('app-index')
export class AppIndex extends LitElement {
  static styles = styles;

  @state() private showNotificationCard = false;
  @state() private notificationPermission: NotificationPermission = 'default';
  @state() private showInstallBanner = false;

  private positionWatcherId: number | null = null;
  private locationPermissionDenied = false;
  private locationRetryCount = 0;
  private locationRetryTimeout: number | null = null;
  private readonly MAX_LOCATION_RETRIES = 3;

  async firstUpdated() {
    router.addEventListener('route-changed', () => {
      if ("startViewTransition" in document) {
        (document as any).startViewTransition(() => this.requestUpdate());
      }
      else {
        this.requestUpdate();
      }
    });

    // Dynamically import components
    await import('./components/permission-card');

    // Check permissions
    await this.checkPermissions();

    // Decide whether to show iOS install banner
    this.evaluateInstallBanner();
  }

  private async checkPermissions() {
    // Check notification permission
    this.notificationPermission = typeof Notification !== 'undefined' ? Notification.permission : 'denied';
    this.showNotificationCard = this.notificationPermission === 'default';
  }

  private async handleNotificationPermissionRequest() {
    try {
      const subscriptionService = SubscriptionService.getInstance();

      // Request browser permission
      const permission = await subscriptionService.requestPermission();
      this.notificationPermission = permission;

      if (permission === 'granted') {
        // Get VAPID public key from environment
        const vapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
        if (vapidKey) {
          // Create push subscription
          const subscription = await subscriptionService.subscribeToPushNotifications(vapidKey);
          if (subscription) {
            console.log('Successfully subscribed to push notifications from permission card');
          }
        } else {
          console.warn('VAPID public key not found in environment variables');
        }
      }

      this.showNotificationCard = permission === 'default';
      this.requestUpdate();
    } catch (error) {
      console.error('Notification permission error:', error);
      this.showNotificationCard = false;
      this.requestUpdate();
    }
  }

  private handlePermissionCardDismissed(event: CustomEvent) {
    const { type } = event.detail;
    if (type === 'notification') {
      this.showNotificationCard = false;
    }
    this.requestUpdate();
  }

  private handlePermissionActionRequested(event: CustomEvent) {
    const { type } = event.detail;
    if (type === 'notification') {
      this.handleNotificationPermissionRequest();
    }
  }

  private isIosSafari(): boolean {
    const ua = window.navigator.userAgent;
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    const isSafari = /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua);
    return isIOS && isSafari;
  }

  private isInstalled(): boolean {
    return (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
      (navigator as any).standalone === true;
  }

  private evaluateInstallBanner() {
    if (!this.isIosSafari() || this.isInstalled()) {
      this.showInstallBanner = false;
      return;
    }

    const cooldownKey = 'ios-install-banner-dismissed-at';
    const dismissedAtRaw = localStorage.getItem(cooldownKey);
    if (dismissedAtRaw) {
      const dismissedAt = parseInt(dismissedAtRaw, 10);
      const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
      if (!isNaN(dismissedAt) && Date.now() - dismissedAt < sevenDaysMs) {
        this.showInstallBanner = false;
        return;
      }
    }

    this.showInstallBanner = true;
  }

  private async handleInstallBannerDismissed() {
    localStorage.setItem('ios-install-banner-dismissed-at', String(Date.now()));
    this.showInstallBanner = false;
    this.requestUpdate();
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('visibilitychange', this.handleVisibilityOrFocus);
    window.addEventListener('focus', this.handleVisibilityOrFocus);

    // Initialize location tracking
    this.requestLocation();
  }

  private async requestLocation() {
    if (!navigator.geolocation) {
      console.warn('Geolocation not supported');
      return;
    }

    const options: PositionOptions = {
      enableHighAccuracy: false,
      timeout: 30000,
      maximumAge: 60000
    };

    // Try getCurrentPosition first (more reliable in production)
    try {
      console.debug('Attempting to get initial position...');
      const position = await this.getCurrentPositionPromise(options);
      await this.processLocationUpdate(position);
      this.locationRetryCount = 0; // Reset on success

      console.debug('Initial position acquired, starting continuous watch...');
      // Now start continuous watching
      this.startLocationWatch(options);
    } catch (error) {
      console.warn('Initial location acquisition failed:', error);
      this.handleLocationError(error as GeolocationPositionError);
    }
  }

  private getCurrentPositionPromise(options: PositionOptions): Promise<GeolocationPosition> {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, options);
    });
  }

  private startLocationWatch(options: PositionOptions) {
    // Clear any existing watch
    if (this.positionWatcherId !== null) {
      navigator.geolocation.clearWatch(this.positionWatcherId);
    }

    this.positionWatcherId = navigator.geolocation.watchPosition(
      async (position) => {
        await this.processLocationUpdate(position);
        this.locationRetryCount = 0; // Reset retry counter on success
      },
      (error) => this.handleLocationError(error),
      options
    );
  }

  private handleLocationError(error: GeolocationPositionError) {
    console.warn('Location error:', error.code, error.message);

    // In Safari and Firefox this is the only way to detect denied permission
    // PermissionAPI will only report prompt or granted
    if (error.code === error.PERMISSION_DENIED) {
      this.locationPermissionDenied = true;
      window.dispatchEvent(new CustomEvent('location-permission', {
        detail: { granted: false }
      }));
      return; // Don't retry on permission denied
    }

    if (error.code === error.TIMEOUT) {
      // Clear watch on timeout
      if (this.positionWatcherId !== null) {
        navigator.geolocation.clearWatch(this.positionWatcherId);
        this.positionWatcherId = null;
      }

      // Retry with exponential backoff if under max retries
      if (this.locationRetryCount < this.MAX_LOCATION_RETRIES) {
        this.locationRetryCount++;
        const delay = Math.min(2000 * Math.pow(2, this.locationRetryCount - 1), 15000);

        console.log(`Retrying location (attempt ${this.locationRetryCount}/${this.MAX_LOCATION_RETRIES}) in ${delay}ms`);

        this.locationRetryTimeout = window.setTimeout(() => {
          this.requestLocation();
        }, delay);
      } else {
        console.warn('Max location retries reached');
      }
    }
  }

  async processLocationUpdate(position: GeolocationPosition) {
    const location = {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude
    };
    console.debug(`📍 Processing location update at ${new Date().toLocaleTimeString()}: ${location.latitude}, ${location.longitude}`);
    await LocationService.getInstance().processUserLocation(location);
    window.dispatchEvent(new CustomEvent('location-update', {
      detail: location
    }));
  }

  disconnectedCallback() {
    document.removeEventListener('visibilitychange', this.handleVisibilityOrFocus);
    window.removeEventListener('focus', this.handleVisibilityOrFocus);

    // Clear position watcher
    if (this.positionWatcherId !== null) {
      navigator.geolocation.clearWatch(this.positionWatcherId);
      this.positionWatcherId = null;
    }

    // Clear retry timeout
    if (this.locationRetryTimeout !== null) {
      clearTimeout(this.locationRetryTimeout);
      this.locationRetryTimeout = null;
    }

    super.disconnectedCallback();
  }

  private handleVisibilityOrFocus = async () => {
    // Re-evaluate install banner when app regains focus
    this.evaluateInstallBanner();

    // Restart location watching if page becomes visible and watch was stopped,
    // but don't re-prompt for a permission the user already denied.
    if (
      document.visibilityState === 'visible' &&
      this.positionWatcherId === null &&
      !this.locationPermissionDenied
    ) {
      console.debug('Page visible and no active location watch, restarting...');
      this.locationRetryCount = 0; // Reset retry count
      this.requestLocation();
    }
  };

  render() {
    return html`
      ${this.showNotificationCard ? html`
        <permission-card
          .title=${t('notificationsTitle')}
          .message=${t('notificationsMessage')}
          .actionText=${t('enableNotificationsAction')}
          .type=${'notification'}
          @permission-card-dismissed=${this.handlePermissionCardDismissed}
          @permission-action-requested=${this.handlePermissionActionRequested}
        ></permission-card>
      ` : ''}

      ${this.showInstallBanner ? html`
        ${(() => { import('./components/install-banner'); return ''; })()}
        <install-banner
          .title=${t('installOnIOS')}
          .message=${t('installIOSMessage')}
          @install-banner-dismissed=${this.handleInstallBannerDismissed}
        ></install-banner>
      ` : ''}

      ${router.render()}
    `;
  }
}
